import * as CL from '@design-system/component-library';
import { useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useRouteLoaderData } from 'react-router';
import { paths } from '../../common/constants/pathVariables.js';
import { fetchNavigation } from '../../common/fetch.js';
import {
  Locale,
  contactInfoMsg,
  cookieSettingsMsg,
  copyrightMsg,
  elisaCorporationMsg,
  elisaShopsMsg,
  getLocaleFromPath,
  privacyPolicyMsg,
  t,
  termsAndConditionsMsg,
  termsOfUseMsg,
} from '../../common/i18n';
import { isDefined } from '../../common/utils/objectUtils.js';
import { SiteContext } from '../../public/site/SiteContext.js';
import type { NavigationResponse } from '../../generated/api/navigationResponse';

import './Footer.scss';

interface FooterProps {
  isMaintenanceModeForCmsPages: boolean;
}

const defaultSocialLinks = {
  facebook: 'https://www.facebook.com/elisasuomi',
  instagram: 'https://www.instagram.com/elisaoyj',
  linkedin: 'https://www.linkedin.com/company/elisa',
  tiktok: 'https://www.tiktok.com/@elisaoyj',
  twitter: 'https://twitter.com/elisaoyj',
  youtube: 'https://www.youtube.com/user/elisa',
};

const resolveValue = (value: string | undefined, fallback: string) => {
  return value?.trim() ? value : fallback;
};

const mapFooterColumnToMegastack = (column: { title?: string; links?: Array<{ title?: string; url?: string }> }) => {
  const title = column.title?.trim();
  if (!title) {
    return undefined;
  }

  const pages = (column.links ?? [])
    .map(link => {
      const linkTitle = link.title?.trim();
      if (!linkTitle) {
        return undefined;
      }

      const linkUrl = link.url?.trim();
      return linkUrl ? { title: linkTitle, url: linkUrl } : undefined;
    })
    .filter(isDefined);

  return {
    megastack: [{ title, pages }],
  };
};

export const Footer = ({ isMaintenanceModeForCmsPages = false }: FooterProps) => {
  const { siteBaseUrl, siteLanguage } = useContext(SiteContext);
  const { pathname } = useLocation();

  const rootNavigationData = useRouteLoaderData('site-navigation') as NavigationResponse | undefined;
  const [navigationData, setNavigationData] = useState(rootNavigationData);
  const previousSiteLanguage = useRef(siteLanguage);

  useEffect(() => {
    setNavigationData(rootNavigationData);
  }, [rootNavigationData]);

  useEffect(() => {
    const requiresNavigationRefresh = [paths.SELF_SERVICE_HOME, paths.EMPLOYEE_HOME].some(path =>
      pathname.startsWith(path)
    );
    if (previousSiteLanguage.current === siteLanguage || getLocaleFromPath(pathname) || !requiresNavigationRefresh) {
      previousSiteLanguage.current = siteLanguage;
      return;
    }
    previousSiteLanguage.current = siteLanguage;

    fetchNavigation({ locale: siteLanguage })
      .then(setNavigationData)
      .catch(() => setNavigationData(rootNavigationData));
  }, [pathname, rootNavigationData, siteLanguage]);

  const footer = navigationData?.footer;
  const footerSocialLinks = footer?.socialLinks;
  const copyrightYear = footer?.copyrightYear ?? new Date().getFullYear().toString();
  const footerDisclaimerLinks = footer?.disclaimerLinks;
  const corporationLink = siteLanguage === Locale.FI ? 'https://elisa.fi/yhtiotieto/' : 'https://elisa.com/corporate/';
  const footerColumns = (footer?.columns ?? []).map(mapFooterColumnToMegastack).filter(isDefined);
  const publicPageLinks = !isMaintenanceModeForCmsPages
    ? [
        {
          megastack: [
            {
              title: t.SSBV(elisaShopsMsg),
              url: `${siteBaseUrl}/myymalat`,
            },
          ],
        },
        {
          megastack: [
            {
              title: t.GJS9(contactInfoMsg),
              url: `${siteBaseUrl}/yhteystiedot`,
            },
          ],
        },
      ]
    : [];

  return (
    <CL.Footer
      className="of-footer"
      logo={<CL.FooterLogo linkHref={siteBaseUrl} />}
      socialLinks={
        <CL.FooterSocialLinks
          links={{
            facebook: resolveValue(footerSocialLinks?.facebookUrl, defaultSocialLinks.facebook),
            instagram: resolveValue(footerSocialLinks?.instagramUrl, defaultSocialLinks.instagram),
            linkedin: resolveValue(footerSocialLinks?.linkedinUrl, defaultSocialLinks.linkedin),
            tiktok: resolveValue(footerSocialLinks?.tiktokUrl, defaultSocialLinks.tiktok),
            twitter: resolveValue(footerSocialLinks?.twitterUrl, defaultSocialLinks.twitter),
            youtube: resolveValue(footerSocialLinks?.youtubeUrl, defaultSocialLinks.youtube),
          }}
        />
      }
      links={
        <CL.FooterLinks
          links={
            footerColumns.length
              ? footerColumns
              : [
                  {
                    megastack: [
                      {
                        title: 'Elisa.fi',
                        url: '//elisa.fi',
                      },
                    ],
                  },
                  {
                    megastack: [
                      {
                        title: t.FM7B(elisaCorporationMsg),
                        url: corporationLink,
                      },
                    ],
                  },
                  ...publicPageLinks,
                ]
          }
        />
      }
      disclaimerLinks={
        <CL.FooterDisclaimerLinks
          links={{
            TOUL: {
              TOULText: resolveValue(footerDisclaimerLinks?.links?.[0]?.title, t.TMN1(termsOfUseMsg)), // eslint-disable-line @typescript-eslint/naming-convention
              TOULUrl: resolveValue(footerDisclaimerLinks?.links?.[0]?.url, 'https://elisa.fi/kayttoehdot/'), // eslint-disable-line @typescript-eslint/naming-convention
            },
            contract: {
              contractText: resolveValue(footerDisclaimerLinks?.links?.[1]?.title, t.BCPB(termsAndConditionsMsg)),
              contractUrl: resolveValue(footerDisclaimerLinks?.links?.[1]?.url, 'https://elisa.fi/sopimusehdot/'),
            },
            privacy: {
              privacyText: resolveValue(footerDisclaimerLinks?.links?.[2]?.title, t.X924(privacyPolicyMsg)),
              privacyUrl: resolveValue(footerDisclaimerLinks?.links?.[2]?.url, 'https://elisa.fi/tietosuojakaytanto/'),
            },
            cookiesettings: {
              text: resolveValue(footerDisclaimerLinks?.cookieSettingsText, t.VOV7(cookieSettingsMsg)),
            },
            copyright: {
              copyrightText: t.YLFT(copyrightMsg, copyrightYear),
            },
          }}
        />
      }
    />
  );
};
